/**
 * Maps a failed buyer save to the copy shown to the person doing the saving.
 *
 * Two failures are worth naming, because each has a different next step and both
 * are reachable from the address form.
 *
 * 1. **The Google place is taken.** `buyerUpdate` looks the place up before it
 *    writes anything (`updateBuyer.ts:59-70`, google-sourced payloads only) and
 *    answers 409 with `EXPECTED_ERRORS.DuplicateGooglePlaceId`, whose message is
 *    verbatim:
 *
 *      "Duplicate: A Buyer for this Google Place already exists."
 *
 *    The next step is to look at the other buyer, not to retry.
 *
 * 2. **The Google lookup failed.** Every save carrying `addressSource: 'google'`
 *    makes a live Place Details call for the payload's `googlePlaceId`
 *    (`getAddressByGooglePlaceId`, 1s timeout) and the handler wraps any throw in
 *    a blanket 500 whose message is just "Something went wrong". A retired place
 *    id is the common cause. The recovery is to switch the buyer to a custom
 *    address, and that genuinely works in this failure mode: the handler branches
 *    on the *incoming* payload's `addressSource`, so a custom save never makes
 *    the Google call at all.
 *
 * The message text is all there is to branch on — the resolver's response
 * template forwards message/type/data through `$util.error`, but Apollo only
 * surfaces the message on `GraphQLError.message`. Everything else (the
 * validator's bare `Bad Request`, `Forbidden`, a dropped connection) is written
 * for engineers and is deliberately not shown; the generic copy stays honest
 * about not knowing the cause. The raw text still reaches the console through
 * the Apollo error link.
 *
 * Case 2 needs the handler's own 500 as well as a Google payload. The handler
 * wraps everything it throws in that one message, so it is not proof the Google
 * call is what failed, but it does rule out the failures that are definitely
 * something else: a rejected input, a permission error, a request that never
 * arrived. Recommending a custom address for those would send someone down a
 * road that does not fix what is actually broken.
 */

export interface UpdateBuyerFailureCopy {
  title: string;
  message: string;
}

/**
 * Matched as a substring: Apollo and AppSync both wrap the service message
 * (`GraphQL error: `, `Duplicate: `), so an equality check would miss it.
 */
const DUPLICATE_GOOGLE_PLACE_TEXT =
  'A Buyer for this Google Place already exists';

/**
 * `EXPECTED_ERRORS.InternalServerError`, the only 500 the buyer handler emits
 * (buyerService `constants.ts`). Matched as a substring for the same reason.
 */
const INTERNAL_SERVER_ERROR_TEXT = 'Something went wrong';

export const DUPLICATE_BUYER_ADDRESS_FAILURE_COPY: UpdateBuyerFailureCopy = {
  title: 'Address already taken',
  message:
    'Another buyer already uses this Google business address. Open that buyer to check which one is correct.',
};

export const GOOGLE_ADDRESS_UNAVAILABLE_FAILURE_COPY: UpdateBuyerFailureCopy = {
  title: 'Address not saved',
  message:
    'Choco could not confirm this business address with Google. Use a custom address to set it by hand.',
};

export const UNKNOWN_UPDATE_BUYER_FAILURE_COPY: UpdateBuyerFailureCopy = {
  title: 'Buyer not saved',
  message: 'Nothing was saved. Check the details and try again.',
};

export const getUpdateBuyerFailureCopy = ({
  errorMessage,
  sentGoogleAddress,
}: {
  errorMessage: string;
  /**
   * Whether the save that failed carried a Google address. Only then is the
   * Google lookup part of the save, and only then is switching to a custom
   * address a way past it.
   */
  sentGoogleAddress: boolean;
}): UpdateBuyerFailureCopy => {
  if (errorMessage.includes(DUPLICATE_GOOGLE_PLACE_TEXT)) {
    return DUPLICATE_BUYER_ADDRESS_FAILURE_COPY;
  }

  if (sentGoogleAddress && errorMessage.includes(INTERNAL_SERVER_ERROR_TEXT)) {
    return GOOGLE_ADDRESS_UNAVAILABLE_FAILURE_COPY;
  }

  return UNKNOWN_UPDATE_BUYER_FAILURE_COPY;
};
